import { Directive, ElementRef, HostBinding, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

// switch to icon-only buttons once any truncated button title gets narrower than this
const MIN_TITLE_WIDTH = 40;
const OVERFLOW_TOLERANCE = 1;
const ICON_ONLY_CLASS = 'rnpl-collapse-buttons-icon-only';
const BUTTONS_SELECTOR = 'rnpl-nav-bar-actions-new';
const TITLE_SELECTOR = '.btn-title, .content-container .title';

/**
 * Action bar with `rnpl-nav-bar-actions-new` buttons: titles shrink with ellipsis, and when that is
 * not enough the buttons collapse to icons only. Styles — `.rnpl-collapse-buttons` in _table-action-bar.scss.
 */
@Directive({
  selector: '[rnplCollapseButtonsOnOverflow]'
})
export class CollapseButtonsOnOverflowDirective implements OnInit, OnDestroy {

  @HostBinding('class.rnpl-collapse-buttons') readonly hostClass = true;

  private resizeObserver: any;
  private mutationObserver: MutationObserver;
  private iconOnlyWidth = 0;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      if ((window as any).ResizeObserver) {
        this.resizeObserver = new (window as any).ResizeObserver(() => this.update(false));
        this.resizeObserver.observe(host);
      }

      this.mutationObserver = new MutationObserver(() => this.update(true));
      this.mutationObserver.observe(host, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private update(contentChanged: boolean): void {
    const host = this.elementRef.nativeElement;

    if (host.classList.contains(ICON_ONLY_CLASS)) {
      if (!contentChanged && host.clientWidth <= this.iconOnlyWidth) { return; }
      this.renderer.removeClass(host, ICON_ONLY_CLASS);
    }

    if (this.titlesDontFit()) {
      this.iconOnlyWidth = host.clientWidth;
      this.renderer.addClass(host, ICON_ONLY_CLASS);
    }
  }

  private titlesDontFit(): boolean {
    const buttons = this.elementRef.nativeElement.querySelector(BUTTONS_SELECTOR) as HTMLElement;
    if (!buttons) { return false; }
    if (this.buttonsOverflow(buttons)) { return true; }

    const titles = buttons.querySelectorAll(TITLE_SELECTOR);
    for (let i = 0; i < titles.length; i++) {
      const title = titles[i] as HTMLElement;
      const visible = !!title.getClientRects().length;
      if (visible && title.scrollWidth > title.clientWidth && title.clientWidth < MIN_TITLE_WIDTH) {
        return true;
      }
    }
    return false;
  }

  private buttonsOverflow(buttons: HTMLElement): boolean {
    const containerRect = buttons.getBoundingClientRect();
    const children = buttons.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      if (!child.getClientRects().length) { continue; }
      const rect = child.getBoundingClientRect();
      if (rect.right > containerRect.right + OVERFLOW_TOLERANCE || rect.left < containerRect.left - OVERFLOW_TOLERANCE) {
        return true;
      }
    }
    return false;
  }
}
